/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ProgramType } from 'client-code/airing';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { Locale } from '../../../core/locale/Locale';
import { ServicesClientImpl } from '../../../core/impls/ServicesClientImpl';
import {
    CHANNEL_TITLE_MAX_LENGTH,
    PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_1080P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_720P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_1080P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_720P,
    START_RATING_MAX_STARS,
    UP_NEXT_TITLE_MAX_LENGTH,
} from '../../../config';
import { RootState } from '../../../store/root.reducers';
import { Clock } from '../../Clock';
import { ClockTypes } from '../../Clock/ClockTypes';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import { ImageLoader } from '../../ImageLoader';
import { InfoBannerUtils } from '../FullInfoBanner/EventDescription/InfoBannerUtils';
import { StandardInfoBannerProps } from './StandardInfoBannerProps';
import { DeviceModel } from '../../../core/device/DeviceModel';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { PlaybackContentType } from '../../../core/socu/enums/PlaybackContentType';
import { SocuDataModelImpl } from '../../../core/socu/impls';
import { BannerTip } from '../BannerTip';

import './StandardInfoBanner.scss';

const StandardInfoBanner: FunctionComponent<StandardInfoBannerProps> = (props): JSX.Element => {
    const {
        mAiringData,
        mPreviewData,
        hideBannerTipAtProgramBoundary,
        setHideBannerTipAtProgramBoundary,
    } = props;
    const { channel } = useSelector((state: RootState) => state.videoPlayer);
    const enteringChannelNumberEnabled = useSelector(
        (state: RootState) => state.infoBanner.enteringChannelNumberEnabled,
    );
    const [playbackTypeDisplayName, setPlaybackTypeDisplayName] = useState('');
    const { channelNumber, callSign, channelLogoUrl, videoResolution } = channel;

    const { isTrickplayMenuActive, isFullInfoActive, isStandardInfoActive, channelNumberEntered } =
        useSelector((state: RootState) => state.infoBanner);
    const playbackContentType = SocuDataModelImpl.getInstance().getPlaybackContentType();
    const isSocuContentPlaying = playbackContentType !== PlaybackContentType.LIVE;
    const isOnlyStandardInfoActive =
        !isTrickplayMenuActive && !isFullInfoActive && isStandardInfoActive;

    const dataFormatter = new InfoBannerUtils(mAiringData, mPreviewData);
    const services = new ServicesClientImpl();

    const isNew = dataFormatter.hasNew();

    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);

    const isNewNext = dataFormatter.hasNewNext();

    const channelTitle = dataFormatter.getChannelTitleText();

    const channelTitleNext = dataFormatter.getChannelTitleTextNext();

    const upNext = `${Locale.getString(
        LocaleKeys.LIVE_TV_STANDARD_INFO_BANNER_UP_NEXT,
    )} ${channelTitleNext}`;

    const time = dataFormatter.getTime();

    const seasonEpisode = dataFormatter.getSeasonEpisode();

    const isMovieContent = dataFormatter.getProgramType() === ProgramType.Movie;

    let timeDiv;
    if (dataFormatter.getTime())
        timeDiv = <span data-testid="standard-info-banner-time">{time}</span>;

    let seasonEpisodeDiv;
    if (dataFormatter.getSeasonEpisode())
        seasonEpisodeDiv = (
            <span data-testid="standard-info-banner-season-episode">{seasonEpisode}</span>
        );

    const starRating = dataFormatter.getStarRating();
    const starRatingDiv =
        starRating !== 0 ? (
            <span className="star-rating" data-testid="standard-info-banner-star-rating">
                {new Array(Math.floor(starRating)).fill(0).map((_, index) => {
                    return <span className="star-icon full" key={index}></span>;
                })}
                {starRating > Math.floor(starRating) ? (
                    <span className="star-icon half"></span>
                ) : null}
                {new Array(START_RATING_MAX_STARS - Math.floor(starRating))
                    .fill(0)
                    .map((_, index) => {
                        return <span className="star-icon empty" key={index}></span>;
                    })}
            </span>
        ) : null;

    const parentalControlsRating = dataFormatter.getParentalControlsRating();

    const parentalControlsRatingDiv =
        parentalControlsRating &&
        parentalControlsRating.map(
            ({ typeId, valueId }: any, index: React.Key | null | undefined) => {
                return services.getInternalRatingName(typeId + valueId) ? (
                    <span
                        className="parental-controls-rating-text"
                        key={index}
                        data-testid="standard-info-banner-parental-ratings"
                    >
                        {services.getInternalRatingName(typeId + valueId)}
                    </span>
                ) : null;
            },
        );

    const metadataSeperatorDiv = (
        <span data-testid="standard-info-banner-metadata-separator"> | </span>
    );
    const isDataSeperatorRequried = (check: boolean) => {
        return check ? metadataSeperatorDiv : null;
    };

    let fullInfoMetadataDiv;
    if (!isMovieContent) {
        fullInfoMetadataDiv = (
            <div className="metadata" data-testid="standard-info-banner-channel-metadata">
                {timeDiv}
                {isDataSeperatorRequried(dataFormatter.getTimeParentalControlsRatingSeperator())}
                {parentalControlsRatingDiv}
                {isDataSeperatorRequried(
                    dataFormatter.getParentalControlsRatingSeasonEpisodeSeperator(),
                )}
                {seasonEpisodeDiv}
            </div>
        );
    } else {
        fullInfoMetadataDiv = (
            <div className="metadata">
                {timeDiv}
                {isDataSeperatorRequried(dataFormatter.getTimeParentalControlsRatingSeperator())}
                {parentalControlsRatingDiv}
                {isDataSeperatorRequried(
                    dataFormatter.getParentalControlsRatingStarRatingSeperator(),
                )}
                {starRatingDiv}
            </div>
        );
    }

    const upNextTrunactionClass = dataFormatter.hasNewNext()
        ? 'truncate-up-next-with-new'
        : 'truncate-up-next';

    const upNextClasses = `up-next   ${
        upNext.length > UP_NEXT_TITLE_MAX_LENGTH ? upNextTrunactionClass : ''
    }`;
    const channelTitleClasses = `channel-title    ${
        channelTitle.length > CHANNEL_TITLE_MAX_LENGTH ? 'truncate' : ''
    }`;

    const primaryBrandingImageUrl = useRef('');

    const standardInfoBannerDiv = (
        <div
            className={`standard-info-banner ${
                isOnlyStandardInfoActive ? 'only-standard-info-active' : ''
            }`}
            data-testid="standard-info-banner"
        >
            <ImageLoader
                src={primaryBrandingImageUrl.current}
                imageType={ImageLoaderTypes.STANDARD_INFO_BANNER_PARTNER_BRANDING}
            />
            {isSocuContentPlaying ? (
                <ImageLoader
                    imageType={
                        ImageLoaderTypes.STANDARD_INFO_BANNER_PARTNER_BRANDING_WITH_SOCU_ICON
                    }
                />
            ) : null}
            <div className="standard-info-top-row">
                <div
                    className={channelTitleClasses}
                    data-testid="standard-info-banner-channel-title"
                >
                    {channelTitle}
                </div>
                <div className="socu-version">
                    {metadataSeperatorDiv}
                    {playbackTypeDisplayName}
                </div>
                {isNew && (
                    <img
                        src={newIcon?.imageUrl}
                        className={`new-icon ${newIcon?.imageType}`}
                        data-testid={'standard-info-banner-new-icon'}
                    />
                )}
                <div className="channel-info">
                    {channelNumber && !enteringChannelNumberEnabled ? (
                        <div
                            className="channel-number"
                            data-testid="standard-info-banner-channel-number"
                        >
                            {channelNumber}
                        </div>
                    ) : (
                        <span className="enter-channel-number">{channelNumberEntered}_</span>
                    )}
                    {videoResolution ? (
                        <div
                            className={`stream-type-icon ${videoResolution}`}
                            data-testid="standard-info-banner-video-resolution"
                        ></div>
                    ) : null}
                    <ImageLoader
                        src={channelLogoUrl}
                        imageType={ImageLoaderTypes.STANDARD_INFO_BANNER_CHANNEL_BRANDING}
                        placeholder={callSign}
                    />
                </div>
                <div className="clock-container">
                    <Clock
                        type={ClockTypes.WATCH_VIDEO_CLOCK}
                        data-testid="standard-info-banner-clock"
                    />
                </div>
            </div>
            <div className="standard-info-banner-bottom-row">
                {fullInfoMetadataDiv}
                {!isSocuContentPlaying && (
                    <div className="up-next-data">
                        <div className={upNextClasses} data-testid="standard-info-banner-up-next">
                            {upNext}
                        </div>
                        {isNewNext && (
                            <img
                                src={newIcon?.imageUrl}
                                className={`next-new-icon ${newIcon?.imageType}`}
                                data-testid={'standard-info-banner-next-new-icon'}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    useEffect(() => {
        if (
            playbackContentType === PlaybackContentType.STARTOVER_BEGINNING ||
            playbackContentType === PlaybackContentType.STARTOVER_RESUME
        ) {
            setPlaybackTypeDisplayName(
                services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER),
            );
        } else if (
            playbackContentType === PlaybackContentType.CATCHUP_BEGINNING ||
            playbackContentType === PlaybackContentType.CATCHUP_RESUME
        ) {
            setPlaybackTypeDisplayName(
                services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP),
            );
        } else {
            setPlaybackTypeDisplayName(
                Locale.getString(LocaleKeys.LIVE_TV_STANDARD_INFO_BANNER_LIVE_TV_STRING),
            );
        }

        primaryBrandingImageUrl.current = DeviceModel.is1080P()
            ? services.getPartnerBrandingImageUrl(
                  PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_1080P,
                  PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_1080P,
              )
            : services.getPartnerBrandingImageUrl(
                  PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_720P,
                  PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_720P,
              );
        return () => {
            if (hideBannerTipAtProgramBoundary) setHideBannerTipAtProgramBoundary(false);
        };
    }, [playbackContentType]);

    return (
        <div className="standard-info-banner-container">
            <BannerTip hideBannerTipAtProgramBoundary={hideBannerTipAtProgramBoundary} />
            {standardInfoBannerDiv}
        </div>
    );
};

StandardInfoBanner.propTypes = {
    mAiringData: PropTypes.any.isRequired,
    mPreviewData: PropTypes.any.isRequired,
    hideBannerTipAtProgramBoundary: PropTypes.any.isRequired,
    setHideBannerTipAtProgramBoundary: PropTypes.any.isRequired,
};

export default StandardInfoBanner;
